import type { ComponentType, CSSProperties } from 'react';
import { ArrowDownRight, ArrowRight, ArrowUpRight, Minus, type LucideIcon } from 'lucide-react';
import type { Kpi } from '@odd/domain';
import { ErpLink } from '../components/ui';

interface CompactPulseProps {
  kpis: Kpi[];
  allSegments: Kpi[] | null;
  icons: Record<Kpi['id'], LucideIcon>;
  pendingMinutesUrl: string;
  onTreasury: () => void;
}

export function CompactPulse({ kpis, allSegments, icons, pendingMinutesUrl, onTreasury }: CompactPulseProps) {
  return (
    <section
      className="v2-pulse"
      aria-label="Pulso de la oficina"
      style={
        {
          '--kpi-count': kpis.length,
          '--kpi-tablet-count': Math.min(3, kpis.length),
          '--kpi-mobile-count': Math.min(2, kpis.length),
        } as CSSProperties
      }
    >
      {kpis.map((k) => {
        const Icon = icons[k.id];
        const red = Number(k.sub.split(' ')[0]) || 0;
        const total = allSegments?.find((item) => item.id === k.id)?.value ?? k.value;
        const treasury = k.id === 'K6' && k.available;
        const deltaLabel =
          k.delta == null
            ? ''
            : k.delta === 0
              ? 'Igual que la semana pasada'
              : `${Math.abs(k.delta)} ${k.delta > 0 ? 'más' : 'menos'} que la semana pasada`;
        const TrendIcon: ComponentType<{ size?: number; 'aria-hidden'?: boolean }> =
          k.delta === 0 ? Minus : (k.delta ?? 0) > 0 ? ArrowUpRight : ArrowDownRight;
        const content = (
          <>
            <span className="v2-kpi-heading">
              <Icon size={14} strokeWidth={1.5} aria-hidden="true" />
              <span>{k.label}</span>
            </span>
            <span className="v2-kpi-value-row">
              <strong className="v2-kpi-value">{k.available ? k.value.toLocaleString('es-ES') : '—'}</strong>
              {k.available && k.delta != null && (
                <span
                  className="v2-kpi-trend"
                  data-direction={k.delta > 0 ? 'up' : k.delta < 0 ? 'down' : 'equal'}
                  aria-label={deltaLabel}
                  title={deltaLabel}
                >
                  <TrendIcon size={12} aria-hidden />
                  {k.delta !== 0 && Math.abs(k.delta)}
                </span>
              )}
            </span>
            {k.available ? (
              <>
                <span className="v2-kpi-status" data-light={k.light}>
                  {k.id !== 'K1' && <i aria-hidden="true" />}
                  {k.id === 'K1' ? k.sub : `${red} en rojo`}
                </span>
                <span className="v2-kpi-bottom">
                  {total !== k.value && (
                    <span title={`${total} con todos los segmentos`}>Todos: {total}</span>
                  )}
                  {k.id === 'K2' && (
                    <ErpLink href={pendingMinutesUrl} title="Ver actas pendientes en Eleva">
                      <span>Ver actas</span>
                    </ErpLink>
                  )}
                  {treasury && (
                    <span className="v2-kpi-link">
                      Ver tesorería <ArrowRight size={12} aria-hidden="true" />
                    </span>
                  )}
                </span>
              </>
            ) : (
              <span className="v2-kpi-unavailable">
                Sin datos todavía
                <br />
                {k.sourceLabel}
              </span>
            )}
          </>
        );
        return treasury ? (
          <button
            key={k.id}
            type="button"
            className="v2-kpi"
            onClick={onTreasury}
            aria-label={`${k.label}: ver detalle`}
          >
            {content}
          </button>
        ) : (
          <article key={k.id} className="v2-kpi" aria-label={k.label}>
            {content}
          </article>
        );
      })}
    </section>
  );
}
